import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, API_BASE, getStoredAuth } from "@/lib/apiClient";
import { displaySerial } from "@/lib/serials";
import { formatRam, formatStorage } from "@/lib/specs";
import { PageHeader } from "@/components/DataTableParts";
import { Loader2, Download, ArrowLeft, Truck, Barcode, ClipboardCheck, BatteryFull, History, Pencil, Trash2 } from "lucide-react";
import Modal from "@/components/Modal";
import { useAuth, canReceive, canQCComplete, canBattery, canSerialEntry, canProcure } from "@/lib/auth";

const STATUS_LABEL = {
  DRAFT: "Draft", SUBMITTED: "Submitted",
  PENDING_RECEIVING: "Pending Receiving",
  PENDING_SERIAL_ENTRY: "Pending Serial Entry",
  QC_PENDING: "Pending QC",
  QC_IN_PROGRESS: "QC In Progress",
  PENDING_BATTERY_DETAILS: "Pending Battery Testing",
  READY_FOR_SALE: "Ready for Sale",
  QC_COMPLETED: "QC Completed",
};
const STATUS_COLORS = {
  DRAFT: "bg-slate-200 text-slate-700",
  SUBMITTED: "bg-amber-100 text-amber-800",
  PENDING_RECEIVING: "bg-amber-100 text-amber-800",
  PENDING_SERIAL_ENTRY: "bg-orange-100 text-orange-800",
  QC_PENDING: "bg-blue-100 text-blue-800",
  QC_IN_PROGRESS: "bg-sky-100 text-sky-800",
  PENDING_BATTERY_DETAILS: "bg-indigo-100 text-indigo-800",
  READY_FOR_SALE: "bg-emerald-100 text-emerald-800",
  QC_COMPLETED: "bg-emerald-100 text-emerald-800",
};

const GENERATION_OPTIONS = [
  "1st Gen","2nd Gen","3rd Gen","4th Gen","5th Gen","6th Gen","7th Gen",
  "8th Gen","9th Gen","10th Gen","11th Gen","12th Gen","13th Gen","14th Gen",
];

const PKIND = { intel_core_i5: "Intel Core i5", intel_core_i7: "Intel Core i7", intel_core_i3: "Intel Core i3", amd: "AMD", ryzen: "Ryzen", others: "Others" };
const COND = { painted: "Painted", unpainted: "Unpainted", require_paint: "Require Paint" };

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("en-IN") : "—");
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");
const fmtMoney = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);

export default function PurchaseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [openPanel, setOpenPanel] = useState(null); // "receive" | "serial" | "qc" | "battery"
  // V2.1 — Delete Purchase (DRAFT only, SUPER_ADMIN). Confirmation dialog
  // + backend enforcement; failures land in `deleteError` shown inside the
  // modal so Reception sees why the delete was blocked.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, tlRes, csRes] = await Promise.all([
        api.get(`/purchases/${id}`),
        api.get(`/purchases/${id}/workflow`).catch(() => ({ data: [] })),
        api.get(`/company-settings`).catch(() => ({ data: null })),
      ]);
      setData(dRes.data);
      setTimeline(tlRes.data || []);
      setCompanySettings(csRes.data);
    }
    catch (e) { setErr(e?.response?.data?.detail?.message || e?.message || "Failed to load"); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const downloadInvoice = async () => {
    if (!data?.invoice_url) return;
    const auth = getStoredAuth();
    const url = `${API_BASE.replace(/\/api$/, "")}${data.invoice_url}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${auth?.access_token}` } });
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = `${data.purchase_id}-invoice`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(objectUrl);
  };

  // Inventory-status roll-up across all passports created for this purchase.
  const inventoryStatusSummary = useMemo(() => {
    if (!data?.units) return {};
    const counts = {};
    data.units.forEach((u) => {
      const s = u.passport_status || (u.passport_id ? "CREATED" : "NOT_YET");
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  }, [data]);

  const status = data?.status;
  // V1.6 — Virtual header status for display only. When the DB header is
  // still QC_PENDING but at least one unit has already crossed QC (moved to
  // PENDING_BATTERY_DETAILS / READY_FOR_SALE), surface it as "QC In Progress"
  // in the UI. Enable/disable logic still keys off the real DB status.
  const displayStatus = useMemo(() => {
    if (status === "QC_PENDING" && (data?.units || []).some((u) =>
      u.passport_status && ["PENDING_BATTERY_DETAILS", "READY_FOR_SALE"].includes(u.passport_status)
    )) {
      return "QC_IN_PROGRESS";
    }
    return status;
  }, [status, data]);
  const canRecv = canReceive(user) && (status === "SUBMITTED" || status === "PENDING_RECEIVING");
  const canSer = canSerialEntry(user, companySettings?.serial_entry_role || "WAREHOUSE") && status === "PENDING_SERIAL_ENTRY";
  const canQCEnd = canQCComplete(user) && status === "QC_PENDING";
  const canBat = canBattery(user) && status === "PENDING_BATTERY_DETAILS";
  // V1.2 — Edit is allowed for RECEPTION/ADMIN/SUPER_ADMIN while the purchase
  // is DRAFT or PENDING_RECEIVING. After Receive Stock is completed, editing
  // is locked automatically (backend also enforces this).
  const canEdit = canProcure(user) && (status === "DRAFT" || status === "PENDING_RECEIVING");
  // V2.2 — Delete is available to SUPER_ADMIN and RECEPTION. The DRAFT-only
  // restriction has been removed; the backend blocks deletion for any purchase
  // that has downstream activity (received stock, QC, inventory, sales).
  const canDelete = user?.role === "SUPER_ADMIN" || user?.role === "RECEPTION";

  const performDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await api.delete(`/purchases/${data.id}`, { data: {} });
      navigate("/admin/purchases", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : (detail?.message || e?.message || "Could not delete purchase");
      setDeleteError(msg);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="text-center py-16 text-slate-500"><Loader2 className="inline animate-spin" size={20}/> Loading…</div>;
  if (err) return <div className="text-rose-600">{err}</div>;
  if (!data) return null;

  return (
    <div>
      <div className="mb-3">
        <Link to="/admin/purchases" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="btn-back">
          <ArrowLeft size={14}/> Back to purchases
        </Link>
      </div>
      <PageHeader
        title={<span className="font-mono">{data.purchase_id}</span>}
        subtitle={<>
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[displayStatus] || "bg-slate-100"}`} data-testid="purchase-status">
            {STATUS_LABEL[displayStatus] || displayStatus}
          </span>
          <span className="ml-2 text-sm text-slate-500">{data.vendor_name}</span>
        </>}
        actions={data.invoice_url ? (
          <button onClick={downloadInvoice} data-testid="btn-download-invoice"
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 hover:bg-slate-100 px-3 py-1.5 text-sm">
            <Download size={14}/> Download Invoice
          </button>
        ) : null}
      />

      {/* Workflow action buttons — render only if role+status permits */}
      <div className="mb-4 flex flex-wrap gap-2" data-testid="workflow-actions">
        {canEdit && (
          <button onClick={() => navigate(`/admin/purchases/new?draftId=${data.id}`)}
            className="inline-flex items-center gap-1.5 rounded-md bg-slate-800 hover:bg-slate-900 text-white px-3 py-1.5 text-sm"
            data-testid="btn-edit-purchase">
            <Pencil size={14}/> Edit Purchase
          </button>
        )}
        {canDelete && (
          <button onClick={() => { setDeleteError(""); setConfirmDelete(true); }}
            className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 text-rose-700 hover:bg-rose-50 px-3 py-1.5 text-sm"
            data-testid="btn-delete-purchase">
            <Trash2 size={14}/> Delete Purchase
          </button>
        )}
        {/* V1.6 — Simplified workflow: exactly two buttons.
             Pending Receiving → Receive Stock enabled, QC disabled.
             Pending QC / QC In Progress → Receive Stock disabled, QC enabled.
             QC opens the unit-based QC page (/unit-flow) where each unit is
             processed independently — completing one unit advances only that
             unit to Pending Battery Testing while the rest stay Pending QC. */}
        {(canReceive(user) || canQCComplete(user)) && (() => {
          const receivePhase = status === "SUBMITTED" || status === "PENDING_RECEIVING";
          // QC phase: after Receive Stock. New purchases land in QC_PENDING
          // directly; legacy purchases still in PENDING_SERIAL_ENTRY are also
          // handled by the same unit-flow QC screen.
          const qcPhase = status === "QC_PENDING" || status === "PENDING_SERIAL_ENTRY";
          const receiveEnabled = canReceive(user) && receivePhase;
          const qcEnabled = canQCComplete(user) && qcPhase;
          return (
            <>
              <button onClick={() => receiveEnabled && setOpenPanel("receive")}
                disabled={!receiveEnabled}
                aria-disabled={!receiveEnabled}
                title={receiveEnabled ? "Receive Stock" : "Receive Stock is not available at this stage"}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm ${
                  receiveEnabled
                    ? "bg-amber-600 hover:bg-amber-700 text-white"
                    : "bg-slate-100 text-slate-400 cursor-not-allowed"
                }`}
                data-testid="btn-receive">
                <Truck size={14}/> Receive Stock
              </button>
              <button onClick={() => qcEnabled && navigate(`/admin/purchases/${data.id}/qc-job`)}
                disabled={!qcEnabled}
                aria-disabled={!qcEnabled}
                title={qcEnabled ? "Open the consolidated Purchase QC Job" : "Complete Receive Stock first"}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm ${
                  qcEnabled
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-slate-100 text-slate-400 cursor-not-allowed"
                }`}
                data-testid="btn-open-qc-job">
                <ClipboardCheck size={14}/> Open Purchase QC Job
              </button>
            </>
          );
        })()}
        {canBat && (
          <button onClick={() => setOpenPanel("battery")}
            className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 text-sm"
            data-testid="btn-battery">
            <BatteryFull size={14}/> Enter Battery Details
          </button>
        )}
      </div>

      {/* Purchase header + configuration */}
      <section className="bg-white border border-slate-200 rounded-md p-4 mb-4" data-testid="purchase-header">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Header</h3>
        <div className="grid grid-cols-4 gap-4 text-sm">
          <Item label="Purchase Date" value={fmtDate(data.purchase_date)}/>
          <Item label="Vendor" value={data.vendor_name}/>
          <Item label="Vendor Warranty" value={`${data.vendor_warranty_days} days`}/>
          <Item label="Invoice" value={data.invoice_no || "—"}/>
          <Item label="Invoice Date" value={fmtDate(data.invoice_date)}/>
          <Item label="Purchase Amount" value={fmtMoney(data.purchase_amount)}/>
          <Item label="Freight" value={fmtMoney(data.freight)}/>
          <Item label="Other Charges" value={fmtMoney(data.other_charges)}/>
          <Item label="GST" value={fmtMoney(data.gst_amount)}/>
          <Item label="Total" value={<span className="font-semibold text-emerald-700">{fmtMoney(data.total_amount)}</span>}/>
          <Item label="Submitted At" value={fmtDateTime(data.submitted_at)}/>
          <Item label="Total Units" value={data.total_units}/>
        </div>
        {data.remarks && <div className="mt-3 text-sm"><span className="text-slate-500">Remarks:</span> {data.remarks}</div>}
      </section>

      {/* Inventory Status roll-up */}
      {Object.keys(inventoryStatusSummary).length > 0 && (
        <section className="bg-white border border-slate-200 rounded-md p-4 mb-4" data-testid="inventory-status-summary">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Current Inventory Status</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(inventoryStatusSummary).map(([s, n]) => (
              <span key={s} className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[s] || "bg-slate-100 text-slate-700"}`}>
                {STATUS_LABEL[s] || s} · {n}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Full laptop config table */}
      <section className="bg-white border border-slate-200 rounded-md p-4 mb-4">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Line Items · Full Configuration</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="units-table">
            <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
              <tr>
                <th className="text-left px-2 py-2">Item Type</th>
                <th className="text-left px-2 py-2">Serial</th>
                <th className="text-left px-2 py-2">Barcode</th>
                <th className="text-left px-2 py-2">Brand</th>
                <th className="text-left px-2 py-2">Model</th>
                <th className="text-left px-2 py-2">Processor</th>
                <th className="text-left px-2 py-2">Generation</th>
                <th className="text-right px-2 py-2">RAM</th>
                <th className="text-right px-2 py-2">Storage</th>
                <th className="text-left px-2 py-2">Storage Type</th>
                <th className="text-right px-2 py-2">Screen Size</th>
                <th className="text-left px-2 py-2">Condition</th>
                <th className="text-left px-2 py-2">Accessories</th>
                <th className="text-right px-2 py-2">Quantity</th>
                <th className="text-right px-2 py-2">Unit Cost</th>
                <th className="text-left px-2 py-2">Current Status</th>
              </tr>
            </thead>
            <tbody>
              {data.units.map((u) => {
                const itemType = (u.item_type || "laptop").toLowerCase();
                const isLaptop = itemType === "laptop";
                const isBattery = itemType === "battery";
                const isAggregate = !isLaptop && !isBattery;
                const processorLabel = u.processor_kind === "others"
                  ? (u.processor_specify || u.processor || "-")
                  : (PKIND[u.processor_kind] || u.processor || "-");
                const conditionLabel = COND[u.condition] || (u.cosmetic_grade ? `Grade ${u.cosmetic_grade}` : "-");
                const accessories = [
                  u.has_charger && "Charger",
                  u.has_battery && "Battery",
                  u.has_power_cable && "Power Cable",
                  u.has_touchscreen && "Touchscreen",
                ].filter(Boolean);
                const unitCost = (u.unit_cost != null ? u.unit_cost : u.purchase_cost);
                const qty = (u.quantity != null ? u.quantity : 1);
                return (
                  <tr key={u.id} className="border-t border-slate-100" data-testid={`unit-row-${u.id}`}>
                    <td className="px-2 py-2 text-xs uppercase tracking-wider text-slate-600" data-testid={`unit-item-type-${u.id}`}>
                      <span className={`inline-flex rounded px-1.5 py-0.5 ${isLaptop ? "bg-slate-100 text-slate-700" : "bg-teal-50 text-teal-800 ring-1 ring-teal-200"}`}>{itemType}</span>
                    </td>
                    <td className="px-2 py-2 font-mono text-xs" data-testid={`unit-serial-${u.id}`}>{isAggregate ? "—" : displaySerial(u.manufacturer_serial)}</td>
                    <td className="px-2 py-2 font-mono text-xs" data-testid={`unit-barcode-${u.id}`}>{isAggregate ? "—" : displaySerial(u.internal_barcode)}</td>
                    <td className="px-2 py-2">{u.brand || "-"}</td>
                    <td className="px-2 py-2">{u.model || "-"}</td>
                    <td className="px-2 py-2 text-xs">{isLaptop ? processorLabel : "—"}</td>
                    <td className="px-2 py-2 text-xs">{isLaptop ? (u.processor_generation || "-") : "—"}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{isLaptop && u.ram_gb != null ? formatRam(u.ram_gb) : "—"}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{isLaptop && u.storage_gb != null ? formatStorage(u.storage_gb, null) : "—"}</td>
                    <td className="px-2 py-2 text-xs">{isLaptop ? (u.storage_type || "-") : "—"}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-xs">{isLaptop && u.screen_size_inch ? `${u.screen_size_inch}"` : "—"}</td>
                    <td className="px-2 py-2 text-xs">{isLaptop ? conditionLabel : "—"}</td>
                    <td className="px-2 py-2 text-xs text-slate-600" data-testid={`unit-accessories-${u.id}`}>{isLaptop && accessories.length ? accessories.join(" · ") : "—"}</td>
                    <td className="px-2 py-2 text-right tabular-nums" data-testid={`unit-qty-${u.id}`}>
                      {isAggregate ? (
                        <span className="font-mono text-xs">{u.received_qty || 0}/{qty}</span>
                      ) : qty}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums" data-testid={`unit-cost-${u.id}`}>{unitCost != null ? fmtMoney(unitCost) : "-"}</td>
                    <td className="px-2 py-2" data-testid={`unit-status-${u.id}`}>
                      {u.passport_status ? (
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${STATUS_COLORS[u.passport_status] || "bg-slate-100 text-slate-600"}`}>{STATUS_LABEL[u.passport_status] || u.passport_status}</span>
                      ) : u.passport_id ? (
                        <span className="text-xs text-emerald-700">✓ Created</span>
                      ) : (
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${STATUS_COLORS[status] || "bg-slate-100 text-slate-600"}`}>
                          {STATUS_LABEL[status] || status || "-"}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Activity Timeline */}
      <section className="bg-white border border-slate-200 rounded-md p-4" data-testid="workflow-timeline">
        <div className="flex items-center gap-2 mb-3">
          <History size={14} className="text-slate-500"/>
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Activity Timeline</h3>
        </div>
        {timeline.length === 0 ? (
          <div className="text-sm text-slate-400 italic">No workflow events recorded yet.</div>
        ) : (
          <ol className="relative border-l-2 border-slate-200 ml-2">
            {timeline.map((e) => (
              <li key={e.id} className="mb-4 ml-4" data-testid={`timeline-event-${e.id}`}>
                <span className="absolute w-3 h-3 bg-slate-900 rounded-full -left-[7px] mt-1.5"></span>
                <div className="flex items-baseline gap-2 text-sm">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[e.to_status] || "bg-slate-100"}`}>{STATUS_LABEL[e.to_status] || e.to_status}</span>
                  {e.from_status && <span className="text-xs text-slate-400">from {STATUS_LABEL[e.from_status] || e.from_status}</span>}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {fmtDateTime(e.created_at)}
                  {(e.actor_full_name || e.actor_username || e.actor_email) && (
                    <span> · {e.actor_full_name || e.actor_username || e.actor_email}</span>
                  )}
                  {e.actor_role && <span> · <span className="font-medium">{e.actor_role}</span></span>}
                </div>
                {e.remarks && <div className="mt-1 text-sm text-slate-700 italic">“{e.remarks}”</div>}
              </li>
            ))}
          </ol>
        )}
      </section>

      {openPanel === "receive" && <ReceiveModal purchase={data} onClose={() => setOpenPanel(null)} onDone={() => { setOpenPanel(null); load(); }}/>}
      {openPanel === "serial" && <SerialEntryModal purchase={data} onClose={() => setOpenPanel(null)} onDone={() => { setOpenPanel(null); load(); }}/>}
      {openPanel === "qc" && <QCCompleteModal purchase={data} onClose={() => setOpenPanel(null)} onDone={() => { setOpenPanel(null); load(); }}/>}
      {openPanel === "battery" && <BatteryModal purchase={data} onClose={() => setOpenPanel(null)} onDone={() => { setOpenPanel(null); load(); }}/>}
      {confirmDelete && (
        <Modal open={confirmDelete} title="Delete purchase?" onClose={() => !deleting && setConfirmDelete(false)}>
          <div data-testid="confirm-delete-purchase" className="space-y-4 text-sm">
            <p className="text-slate-700">
              Delete this purchase? This action cannot be undone.
            </p>
            <p className="text-xs text-slate-500 font-mono">{data.purchase_id}</p>
            {deleteError && (
              <div data-testid="delete-purchase-error"
                className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {deleteError}
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-2">
              <button type="button" onClick={() => setConfirmDelete(false)} disabled={deleting}
                className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-100 disabled:opacity-50">
                Cancel
              </button>
              <button type="button" onClick={performDelete} disabled={deleting}
                data-testid="btn-confirm-delete-purchase"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50">
                <Trash2 size={14}/> {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Item({ label, value }) {
  return (
    <div>
      <div className="text-xs text-slate-500 uppercase mb-1">{label}</div>
      <div className="text-slate-800">{value}</div>
    </div>
  );
}

// ---------- Workflow Modals ----------

function ReceiveModal({ purchase, onClose, onDone }) {
  const [qty, setQty] = useState(purchase.total_units || 1);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post(`/purchases/${purchase.id}/receive`, { received_qty: Number(qty), remarks: remarks || null });
      onDone();
    } catch (e2) { setErr(e2?.response?.data?.detail?.message || e2?.message || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={true} title="Receive Stock" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="receive-form">
        <div className="text-sm text-slate-600">Expected units: <span className="font-medium">{purchase.total_units}</span></div>
        <label className="block text-sm">Received quantity *
          <input type="number" min={1} max={purchase.total_units || 999} value={qty} onChange={(e) => setQty(e.target.value)} required data-testid="input-received-qty"
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1"/>
        </label>
        <label className="block text-sm">Remarks (optional)
          <textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="input-receive-remarks"
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1"/>
        </label>
        {err && <div className="text-sm text-rose-600" data-testid="receive-error">{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-100">Cancel</button>
          <button type="submit" disabled={busy} data-testid="btn-receive-submit"
            className="px-3 py-1.5 text-sm rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="inline animate-spin mr-1"/> : null} Confirm Receipt
          </button>
        </div>
      </form>
    </Modal>
  );
}

function SerialEntryModal({ purchase, onClose, onDone }) {
  const pendingUnits = (purchase.units || []).filter((u) => u.passport_status === "PENDING_SERIAL_ENTRY" && u.passport_id);
  const [entries, setEntries] = useState(() => pendingUnits.map((u) => ({ passport_id: u.passport_id, manufacturer_serial: "", internal_barcode: "", asset_tag: "" })));
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const updateEntry = (i, patch) => setEntries((prev) => prev.map((e, idx) => idx === i ? { ...e, ...patch } : e));
  const submit = async (e) => {
    e.preventDefault();
    if (entries.some((x) => !x.manufacturer_serial || !x.internal_barcode)) { setErr("Serial number and barcode are mandatory for every row."); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/purchases/${purchase.id}/serial-entry`, { entries, remarks: remarks || null });
      onDone();
    } catch (e2) { setErr(e2?.response?.data?.detail?.message || e2?.message || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={true} title="Enter Serial Numbers" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="serial-entry-form">
        <div className="max-h-80 overflow-y-auto border border-slate-200 rounded">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-2 py-1">#</th>
                <th className="text-left px-2 py-1">Serial Number *</th>
                <th className="text-left px-2 py-1">Barcode *</th>
                <th className="text-left px-2 py-1">Asset Tag</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={e.passport_id} className="border-t">
                  <td className="px-2 py-1 text-xs text-slate-500">{i+1}</td>
                  <td className="px-2 py-1"><input required value={e.manufacturer_serial} onChange={(ev) => updateEntry(i, { manufacturer_serial: ev.target.value })} data-testid={`input-serial-${i}`} className="w-full rounded border border-slate-300 px-2 py-1 text-sm"/></td>
                  <td className="px-2 py-1"><input required value={e.internal_barcode} onChange={(ev) => updateEntry(i, { internal_barcode: ev.target.value })} data-testid={`input-barcode-${i}`} className="w-full rounded border border-slate-300 px-2 py-1 text-sm"/></td>
                  <td className="px-2 py-1"><input value={e.asset_tag} onChange={(ev) => updateEntry(i, { asset_tag: ev.target.value })} data-testid={`input-asset-${i}`} className="w-full rounded border border-slate-300 px-2 py-1 text-sm"/></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <label className="block text-sm">Remarks (optional)
          <textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1 w-full rounded border border-slate-300 px-2 py-1"/>
        </label>
        {err && <div className="text-sm text-rose-600" data-testid="serial-error">{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-100">Cancel</button>
          <button type="submit" disabled={busy} data-testid="btn-serial-submit"
            className="px-3 py-1.5 text-sm rounded bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="inline animate-spin mr-1"/> : null} Save Serials
          </button>
        </div>
      </form>
    </Modal>
  );
}

function QCCompleteModal({ purchase, onClose, onDone }) {
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post(`/purchases/${purchase.id}/qc-complete`, { remarks: remarks || null });
      onDone();
    } catch (e2) { setErr(e2?.response?.data?.detail?.message || e2?.message || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={true} title="Complete QC" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="qc-form">
        <p className="text-sm text-slate-600">Confirm that all {purchase.total_units} units have passed QC checks.</p>
        <label className="block text-sm">Remarks (optional)
          <textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="input-qc-remarks"
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1"/>
        </label>
        {err && <div className="text-sm text-rose-600" data-testid="qc-error">{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-100">Cancel</button>
          <button type="submit" disabled={busy} data-testid="btn-qc-submit"
            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="inline animate-spin mr-1"/> : null} Mark QC Complete
          </button>
        </div>
      </form>
    </Modal>
  );
}

function BatteryModal({ purchase, onClose, onDone }) {
  const pendingUnits = (purchase.units || []).filter((u) => u.passport_status === "PENDING_BATTERY_DETAILS" && u.passport_id);
  const [entries, setEntries] = useState(() => pendingUnits.map((u) => ({
    passport_id: u.passport_id,
    manufacturer_serial: u.manufacturer_serial,
    battery_present: true,
    battery_health_pct: "",
    battery_type: "Li-Ion",
    battery_capacity_wh: "",
    battery_cycle_count: "",
  })));
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const updateEntry = (i, patch) => setEntries((prev) => prev.map((e, idx) => idx === i ? { ...e, ...patch } : e));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = { entries: entries.map((e) => ({
        passport_id: e.passport_id,
        battery_present: !!e.battery_present,
        battery_health_pct: e.battery_present && e.battery_health_pct !== "" ? Number(e.battery_health_pct) : null,
        battery_type: e.battery_present ? (e.battery_type || null) : null,
        battery_capacity_wh: e.battery_present && e.battery_capacity_wh !== "" ? String(e.battery_capacity_wh) : null,
        battery_cycle_count: e.battery_present && e.battery_cycle_count !== "" ? Number(e.battery_cycle_count) : null,
      })), remarks: remarks || null };
      await api.post(`/purchases/${purchase.id}/battery-details`, payload);
      onDone();
    } catch (e2) { setErr(e2?.response?.data?.detail?.message || e2?.message || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={true} title="Enter Battery Details" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="battery-form">
        <div className="max-h-96 overflow-y-auto space-y-3">
          {entries.map((e, i) => (
            <div key={e.passport_id} className="border border-slate-200 rounded p-3" data-testid={`battery-entry-${i}`}>
              <div className="font-mono text-xs text-slate-600 mb-2">{e.manufacturer_serial}</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <label className="block">Battery Present
                  <select value={e.battery_present ? "yes" : "no"} onChange={(ev) => updateEntry(i, { battery_present: ev.target.value === "yes" })} data-testid={`select-battery-present-${i}`}
                    className="mt-1 w-full rounded border border-slate-300 px-2 py-1">
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </label>
                <label className="block">Battery Health {e.battery_present ? "(%)" : ""}
                  {e.battery_present ? (
                    <input type="number" min={0} max={100} value={e.battery_health_pct} onChange={(ev) => updateEntry(i, { battery_health_pct: ev.target.value })} data-testid={`input-battery-health-${i}`}
                      className="mt-1 w-full rounded border border-slate-300 px-2 py-1"/>
                  ) : (
                    <input value="N/A" disabled data-testid={`input-battery-health-${i}`}
                      className="mt-1 w-full rounded border border-slate-200 bg-slate-100 text-slate-400 px-2 py-1"/>
                  )}
                </label>
                {e.battery_present && (<>
                  <label className="block">Battery Type
                    <select value={e.battery_type} onChange={(ev) => updateEntry(i, { battery_type: ev.target.value })} className="mt-1 w-full rounded border border-slate-300 px-2 py-1">
                      <option>Li-Ion</option><option>Li-Po</option><option>NiMH</option><option>Other</option>
                    </select>
                  </label>
                  <label className="block">Capacity (Wh)
                    <input type="number" step="0.01" value={e.battery_capacity_wh} onChange={(ev) => updateEntry(i, { battery_capacity_wh: ev.target.value })}
                      className="mt-1 w-full rounded border border-slate-300 px-2 py-1"/>
                  </label>
                  <label className="block col-span-2">Cycle Count (optional)
                    <input type="number" value={e.battery_cycle_count} onChange={(ev) => updateEntry(i, { battery_cycle_count: ev.target.value })}
                      className="mt-1 w-full rounded border border-slate-300 px-2 py-1"/>
                  </label>
                </>)}
              </div>
            </div>
          ))}
        </div>
        <label className="block text-sm">Remarks (optional)
          <textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1 w-full rounded border border-slate-300 px-2 py-1"/>
        </label>
        {err && <div className="text-sm text-rose-600" data-testid="battery-error">{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-100">Cancel</button>
          <button type="submit" disabled={busy} data-testid="btn-battery-submit"
            className="px-3 py-1.5 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="inline animate-spin mr-1"/> : null} Save & Mark Ready for Sale
          </button>
        </div>
      </form>
    </Modal>
  );
}
